// LiveClassRoom — Página de la sala. El VIDEO de Agora ya NO se monta aquí:
// vive en <PersistentLiveRoom/> (Layout) para sobrevivir la navegación a otros
// módulos (Bug 3). Esta página solo reserva el "hueco" donde el video se
// acopla, y monta la pizarra, el quiz y las grabaciones alrededor.
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LiveWhiteboard from "@/components/LiveWhiteboard";
import RecorderControls, { stopActiveRecordingAndUpload } from "@/components/RecorderControls";
import RecordingsList from "@/components/RecordingsList";
import LiveQuiz from "@/components/LiveQuiz";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLiveSession } from "@/contexts/LiveSessionContext";
import { toast } from "sonner";
import { ChevronLeft, ExternalLink, Loader2, Radio, Maximize2, PencilRuler, Film, PhoneOff, RefreshCw } from "lucide-react";

export default function LiveClassRoom() {
  const { classId } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { session: liveSession, openSession, closeSession, registerDock } = useLiveSession();
  // v5.7.3 · al «ampliar» desde la mini-ventana la sala abre al instante con los datos ya cargados
  // (antes esperaba otra llamada /join y, si fallaba, quedaba bloqueada).
  const [data, setData] = useState(() => (liveSession && liveSession.classId === classId ? liveSession.data : null));
  const [err, setErr] = useState(null);
  const dataRef = useRef(null);
  dataRef.current = data;
  const [ending, setEnding] = useState(false);
  // C55 — El layout del estudiante es LOCAL y persistente por sesión. El
  // docente NO fuerza la vista del alumno (ni por WS ni por polling), así el
  // ciclo de sincronización (5-6 s) ya no lo "regresa" a la vista previa.
  const [layout, setLayout] = useState(() => {
    try {
      const v = sessionStorage.getItem(`jasq_layout_${classId}`);
      if (v === "video" || v === "split" || v === "board") return v;
    } catch (_) { /* sin storage */ }
    return "video";
  }); // video | split | board
  const [showRecordings, setShowRecordings] = useState(false);
  const [recordingsRefresh, setRecordingsRefresh] = useState(0);
  const whiteboardRef = useRef(null);
  const recordingsRef = useRef(null);
  // v5.6.3 · video flotante sobre la pizarra (movible y con dos tamaños).
  const canvasRef = useRef(null);
  const dockElRef = useRef(null);
  const [pipPos, setPipPos] = useState({ x: 0, y: 0 });
  const [pipBig, setPipBig] = useState(false);
  const pipDrag = useRef(null);
  const lastSharedRef = useRef(null);
  const autoShareRef = useRef(false);

  const rolePrefix = (() => {
    const p = (location.pathname || "/").split("/")[1];
    return ["admin", "franquiciado", "docente", "estudiante"].includes(p) ? `/${p}` : "/admin";
  })();
  const listPath = `${rolePrefix}/clases-en-vivo`;

  // Registrar el hueco donde el video se acopla. En "solo pizarra" el hueco se
  // oculta (el host mantiene el video vivo fuera de pantalla).
  const dockRefCb = useCallback((el) => { dockElRef.current = el || null; registerDock(el || null); }, [registerDock]);

  // A7 — Cronómetro sincronizado con el reloj del SERVIDOR: calculamos el
  // desfase con `server_time` para que todos vean el mismo tiempo transcurrido.
  const clockOffsetRef = useRef(0);
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    if (data?.server_time) {
      const srv = new Date(data.server_time).getTime();
      if (!Number.isNaN(srv)) clockOffsetRef.current = srv - Date.now();
    }
  }, [data?.server_time]);
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const fmtClock = (s) => {
    if (s == null) return null;
    if (s < 0) return "Por iniciar";
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return (h > 0 ? `${String(h).padStart(2, "0")}:` : "") + `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  useEffect(() => {
    if (showRecordings && recordingsRef.current) {
      recordingsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showRecordings]);

  const handleRecordingSaved = async (rec) => {
    setRecordingsRefresh((n) => n + 1);
    try {
      if (whiteboardRef.current?.exportPdf) {
        await whiteboardRef.current.exportPdf({ recordingId: rec?.id, silent: true });
        setRecordingsRefresh((n) => n + 1);
      }
    } catch (_) { /* pizarrón vacío o desmontado */ }
  };

  const changeLayout = (next) => {
    setLayout(next);
    try { sessionStorage.setItem(`jasq_layout_${classId}`, next); } catch (_) { /* sin storage */ }
    // Solo el docente difunde "compartir pizarra" (afecta SU vista y disponibilidad,
    // NO la vista elegida por el estudiante).
    if (data?.is_moderator && whiteboardRef.current?.broadcastShare) {
      whiteboardRef.current.broadcastShare(next !== "video");
    }
  };
  // v5.6.3 · «Compartir pizarra» = pizarra grande + video (docente y alumnos) FLOTANTE.
  const toggleBoardShare = () => changeLayout(layout === "video" ? "board" : "video");
  // C55 — El estudiante gobierna su propia vista. v5.6.3: cuando el docente
  // COMPARTE la pizarra (cambio de «no» a «sí») al alumno que estaba en «Solo video»
  // se le abre la pizarra con el video flotante; si luego elige otra vista se respeta.
  // Al dejar de compartir, vuelve a «Solo video» solo si fue movido automáticamente.
  const handleRemoteShare = useCallback((shared) => {
    if (data?.is_moderator) return;
    const next = !!shared;
    if (lastSharedRef.current === next) return;
    const first = lastSharedRef.current === null;
    lastSharedRef.current = next;
    if (next) {
      setLayout((cur) => {
        if (cur === "video") { autoShareRef.current = true; return "board"; }
        return cur;
      });
    } else if (!first && autoShareRef.current) {
      autoShareRef.current = false;
      setLayout((cur) => (cur === "board" ? "video" : cur));
    }
  }, [data?.is_moderator]);

  const onPipDown = (e) => {
    e.preventDefault();
    pipDrag.current = { sx: e.clientX, sy: e.clientY, ox: pipPos.x, oy: pipPos.y };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* sin captura */ }
  };
  const onPipMove = (e) => {
    const d = pipDrag.current;
    if (!d) return;
    const cv = canvasRef.current?.getBoundingClientRect();
    const bx = dockElRef.current?.getBoundingClientRect();
    let x = d.ox + (e.clientX - d.sx);
    let y = d.oy + (e.clientY - d.sy);
    if (cv && bx) {
      const maxLeft = -(cv.width - bx.width - 24);
      const maxUp = -(cv.height - bx.height - 24);
      x = Math.min(0, Math.max(maxLeft, x));
      y = Math.min(0, Math.max(maxUp, y));
    }
    setPipPos({ x, y });
  };
  const onPipUp = () => { pipDrag.current = null; };

  const fetchRoom = useCallback(async () => {
    setErr(null);
    const isSupervisor = new URLSearchParams(location.search).get("supervisor") === "1";
    try {
      const { data: d } = await api.get(`/live-classes/${classId}/join`, {
        params: isSupervisor ? { supervisor: true } : {},
      });
      setData(d);
      if (!d.external_only) openSession(classId, d, location.pathname + location.search);
    } catch (e) {
      // Si ya hay datos de la sala (reingreso), un fallo momentáneo de red no la bloquea
      if (!dataRef.current) setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error obteniendo la sala");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  useEffect(() => { fetchRoom(); }, [fetchRoom]);

  // Salir de la VISTA. El docente conserva la clase viva en segundo plano
  // (mini-ventana). El estudiante cierra su sesión al salir.
  const leaveView = () => {
    if (!data?.is_moderator) closeSession();
    nav(listPath);
  };

  // FINALIZAR para todos (Bug 1): desconecta a los estudiantes conectados.
  const endForEveryone = async () => {
    if (!window.confirm("¿Finalizar la clase para todos? Se desconectará a los estudiantes conectados.")) return;
    setEnding(true);
    try {
      // v5.7.1 · si hay grabación en curso, detenerla y subirla ANTES de cerrar la clase
      const t = toast.loading("Guardando la grabación…");
      const saved = await Promise.race([
        stopActiveRecordingAndUpload(),
        new Promise((r) => setTimeout(() => r("timeout"), 120000)),
      ]);
      toast.dismiss(t);
      if (saved === "timeout") toast.info("La grabación sigue subiéndose en segundo plano.");
      await api.post(`/live-classes/${classId}/end`);
      toast.success("Clase finalizada. Los estudiantes fueron desconectados.");
      closeSession();
      nav(listPath);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo finalizar. Intenta de nuevo.");
    } finally {
      setEnding(false);
    }
  };

  if (err) {
    return (
      <div className="jasq-card p-10 text-center space-y-3" data-testid="live-room-error">
        <p className="text-base font-bold text-[#A52258]">{err}</p>
        <div className="flex items-center justify-center gap-2">
          <button onClick={fetchRoom} className="jasq-btn-primary inline-flex items-center gap-2" data-testid="live-room-retry">
            <RefreshCw size={14} /> Reintentar
          </button>
          <button onClick={() => nav(listPath)} className="jasq-btn-ghost inline-flex items-center gap-2">
            <ChevronLeft size={14} /> Volver
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="jasq-card p-10 text-center space-y-2" data-testid="live-room-loading">
        <Loader2 size={28} className="mx-auto animate-spin text-[#A43790]" />
        <p className="text-sm text-[#6E7591]">Preparando tu sala…</p>
      </div>
    );
  }

  if (data.external_only) {
    return (
      <div className="jasq-card p-10 text-center space-y-4" data-testid="live-room-legacy">
        <Radio size={32} className="mx-auto text-[#A43790]" />
        <h2 className="font-display text-2xl font-extrabold text-[#1F2547]">Esta clase usa Google Meet (legado)</h2>
        <p className="text-sm text-[#6E7591]">Las clases creadas antes de v3.0.0 abren en una pestaña externa.</p>
        <a href={data.join_url} target="_blank" rel="noreferrer" className="jasq-btn-primary inline-flex items-center gap-2" data-testid="live-room-legacy-open">
          <ExternalLink size={14} /> Abrir clase en Google Meet
        </a>
      </div>
    );
  }

  const boardVisible = layout !== "video";
  // v5.6.3 · en «Solo pizarra» el video NO desaparece: queda flotante sobre la pizarra.
  const floatingVideo = layout === "board";

  const Header = (
    <header className="flex items-center justify-between gap-3 mb-2" data-testid="live-room-header">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790]">{data.provider}</p>
        <h1 className="font-display text-xl md:text-2xl font-extrabold text-[#1F2547] truncate">
          {data.title || "Clase en vivo"} {data.is_moderator && <span className="text-[11px] font-bold align-middle ml-2 px-2 py-0.5 rounded-full bg-[#A43790] text-white">Moderador</span>}
          {data.is_supervisor && <span className="text-[11px] font-bold align-middle ml-2 px-2 py-0.5 rounded-full bg-[#1F2547] text-white inline-flex items-center gap-1" data-testid="supervisor-badge">👁 Supervisor</span>}
        </h1>
        {data.starts_at && (() => {
          const startMs = new Date(data.starts_at).getTime();
          const elapsed = Number.isNaN(startMs) ? null : Math.floor((nowMs + clockOffsetRef.current - startMs) / 1000);
          const label = fmtClock(elapsed);
          if (!label) return null;
          return (
            <span data-testid="class-timer" className="inline-flex items-center gap-1 mt-0.5 text-[11px] font-bold text-[#A52258]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#A52258] animate-pulse" /> {label === "Por iniciar" ? label : `En clase · ${label}`}
            </span>
          );
        })()}
      </div>
      <div className="flex items-center gap-1.5">
        <LiveQuiz classId={classId} canModerate={data.is_moderator || data.is_supervisor} />
        <RecorderControls classId={classId} disabled={!data.is_moderator} onSaved={handleRecordingSaved} />
        <button
          onClick={() => setShowRecordings((s) => !s)}
          data-testid="toggle-recordings"
          className={`jasq-btn-ghost inline-flex items-center gap-1 text-xs ${showRecordings ? "border-[#A43790] text-[#A43790]" : ""}`}
          title="Ver grabaciones de esta clase"
        >
          <Film size={12} /> Grabaciones
        </button>
        {data.is_moderator ? (
          <>
            <button
              onClick={toggleBoardShare}
              data-testid="toggle-board-share"
              className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${layout !== "video" ? "border-[#A43790] bg-[#A43790] text-white" : "border-[#A43790] text-[#A43790] hover:bg-[#FFE9F0]"}`}
              title={layout === "video" ? "Mostrar la pizarra junto al video" : "Ocultar la pizarra y volver a solo video"}
            >
              <PencilRuler size={13} /> {layout === "video" ? "Compartir pizarra" : "Ocultar pizarra"}
            </button>
            {layout !== "video" && (
              <div className="hidden sm:flex items-center bg-[#F7F8FA] rounded-full p-0.5 border border-[#E1E5F0]" data-testid="layout-toggle">
                {["split", "board"].map((opt) => (
                  <button key={opt} onClick={() => changeLayout(opt)}
                    data-testid={`layout-${opt}`}
                    className={`text-[11px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${layout === opt ? "bg-[#1F2547] text-white" : "text-[#4C5478]"}`}>
                    {opt === "split" && <><Maximize2 size={11} /> Dividido</>}
                    {opt === "board" && <><PencilRuler size={11} /> Pizarra + video flotante</>}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          // C55 — Selector de vista del ESTUDIANTE (local y persistente).
          <div className="flex items-center bg-[#F7F8FA] rounded-full p-0.5 border border-[#E1E5F0]" data-testid="student-view-toggle">
            {[
              { k: "video", label: "Solo video", Icon: Radio },
              { k: "split", label: "Dividida", Icon: Maximize2 },
              { k: "board", label: "Pizarra + video flotante", Icon: PencilRuler },
            ].map(({ k, label, Icon }) => (
              <button key={k} onClick={() => changeLayout(k)}
                data-testid={`student-view-${k}`}
                title={label}
                className={`text-[11px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${layout === k ? "bg-[#1F2547] text-white" : "text-[#4C5478] hover:text-[#1F2547]"}`}>
                <Icon size={11} /> <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        )}
        {data.is_moderator && (
          <button onClick={endForEveryone} disabled={ending} className="inline-flex items-center gap-1 text-xs font-extrabold px-3 py-1.5 rounded-full bg-[#A52258] text-white hover:bg-[#8A1C49] disabled:opacity-60" data-testid="live-room-end">
            <PhoneOff size={12} /> Finalizar clase
          </button>
        )}
        <button onClick={leaveView} className="jasq-btn-ghost inline-flex items-center gap-1 text-xs" data-testid="live-room-leave" title={data.is_moderator ? "Salir de la vista (la clase sigue activa)" : "Salir de la clase"}>
          <ChevronLeft size={12} /> Salir
        </button>
      </div>
    </header>
  );

  return (
    <div className="space-y-2" data-testid="live-room">
      {Header}
      <div style={{ width: "100%", height: "calc(100vh - 160px)", minHeight: 520, borderRadius: 16, overflow: "hidden", background: "#F7F8FA" }} data-testid="live-room-canvas" ref={canvasRef}>
        <div className="flex flex-col lg:flex-row gap-2 w-full h-full p-1 relative">
          {/* Hueco reservado para el video (el iframe vive en el host persistente) */}
          <div
            ref={dockRefCb}
            data-testid="live-room-dock"
            data-floating={floatingVideo ? "1" : "0"}
            style={floatingVideo ? { transform: `translate(${pipPos.x}px, ${pipPos.y}px)` } : undefined}
            className={[
              "rounded-2xl bg-black grid place-items-center",
              floatingVideo
                ? `absolute right-3 bottom-3 z-30 shadow-2xl border-2 border-[#A43790] ${pipBig ? "w-[min(92%,560px)] h-[330px]" : "w-[min(92%,340px)] h-[230px]"}`
                : `w-full min-h-[260px] h-full ${boardVisible ? "lg:w-1/2" : "lg:w-full"}`,
            ].join(" ")}
          >
            <Loader2 size={24} className="text-white/40 animate-spin" />
          </div>
          {floatingVideo && (
            <div
              style={{ transform: `translate(${pipPos.x}px, ${pipPos.y}px)` }}
              className={`absolute right-3 bottom-3 z-[60] pointer-events-none ${pipBig ? "w-[min(92%,560px)] h-[330px]" : "w-[min(92%,340px)] h-[230px]"}`}
              data-testid="pip-controls"
            >
              <div className="pointer-events-auto absolute top-1 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full bg-[#1F2547]/85 text-white px-2 py-0.5 text-[10px] font-bold backdrop-blur">
                <span
                  onPointerDown={onPipDown} onPointerMove={onPipMove} onPointerUp={onPipUp} onPointerCancel={onPipUp}
                  className="cursor-grab active:cursor-grabbing select-none touch-none px-1" title="Arrastra para mover el video"
                  data-testid="pip-drag"
                >⠿ Mover</span>
                <button type="button" onClick={() => setPipBig((b) => !b)} className="px-1 hover:text-[#F5B82E]" data-testid="pip-size"
                  title={pipBig ? "Hacer más pequeño" : "Hacer más grande"}>{pipBig ? "– Reducir" : "+ Ampliar"}</button>
              </div>
            </div>
          )}
          {/* Pizarra */}
          <div
            className={[
              "w-full min-h-[260px] h-full",
              !boardVisible ? "hidden" : (floatingVideo ? "lg:w-full" : "lg:w-1/2"),
            ].join(" ")}
            data-testid="board-pane-wrapper"
          >
            <LiveWhiteboard
              ref={whiteboardRef}
              classId={classId}
              canModerate={data.is_moderator || data.is_supervisor}
              userName={data.user_display_name}
              onRemoteShare={handleRemoteShare}
            />
          </div>
        </div>
      </div>
      {showRecordings && (
        <div className="mt-4" data-testid="recordings-panel" ref={recordingsRef}>
          <RecordingsList classId={classId} canDelete={data.is_moderator} refreshKey={recordingsRefresh} />
        </div>
      )}
    </div>
  );
}
